import {
  Box,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import useSelectedSector from "hooks/useSelectedSector";
import { useState } from "react";
import TransportDriverService from "services/TransportDriverService";
import { PersonalDataModal } from "./components/PersonalDataModal";
import { CompletedTravelsModal } from "./components/CompletedTravelsModal";
import { Travel } from "types/Travel";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import { Cancel, CheckCircle, DeliveryDining } from "@mui/icons-material";
import TransportDriverMenuItem from "./components/TransportDriverMenuItem";
import { Route, Routes, useNavigate } from "react-router-dom";
import { Row } from "components/common/Layout/Row";
import { toCurrencyFormat } from "utils/formatter";
import CreateDepositModal from "../../OrdersModule/Drivers/CreateDepositModal";
import toast from "react-hot-toast";

export const TransportDrivers = () => {
  const [travelsModal, setTravelsModal] = useState(false);
  const [travels, setTravels] = useState<Travel[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  const { selectedSectorId } = useSelectedSector();

  const sectorIdToSend = showAll ? undefined : selectedSectorId;
  const { data: transportDrivers, refetch: refetchTransportDrivers } =
    TransportDriverService.useGetTransportDriversQuery(
      { sectorId: sectorIdToSend },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const [verifyDriver] = TransportDriverService.useVerifyTransportDriverMutation();
  const [convertToDeliveryDriver] = TransportDriverService.useConvertToDeliveryDriverMutation();

  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<{ id: number; name: string; phone: string } | undefined>(
    undefined,
  );

  const handleOpenTravelsModal = (travels: Travel[]) => {
    setTravelsModal(true);
    setTravels(travels);
  };

  const handleCloseTravelsModal = () => {
    setTravelsModal(false);
    setTravels(null);
  };

  const handleVerifyClick = (transportDriverId: number) => {
    verifyDriver(transportDriverId)
      .then(() => {
        refetchTransportDrivers();
      })
      .catch(() => {
        alert("Error al verificar el conductor");
      });
  };

  const handleConvertToDeliveryDriver = (transportDriverId: number) => {
    convertToDeliveryDriver(transportDriverId)
      .then(() => {
        refetchTransportDrivers();
      })
      .catch(() => {
        alert("Error al convertir en repartidor el conductor");
      });
  };

  const openDetail = (id: number) => {
    navigate(`${id}/personal`);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleOpenModal = (driver: any) => {
    setModalOpen(true);
    setSelectedDriver({
      id: driver.id,
      name: driver.name,
      phone: driver.phone,
    });
  };

  const handleDepositSuccess = () => {
    toast.success("Deposito completado con exito");
    refetchTransportDrivers();
    closeModal();
  };

  return (
    <Box>
      <Row alignItems="center">
        <Typography variant="h5">Lista de conductores</Typography>
        <Row alignItems="center">
          <Typography variant="body1" sx={{ marginRight: 2 }}>
            Todos
          </Typography>
          <Checkbox
            value={showAll}
            onChange={() => {
              setShowAll((prev) => !prev);
            }}
          />
        </Row>
      </Row>
      <TableContainer component={Paper}>
        <Table sx={{ width: 700 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>Nombre</TableCell>
              <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>Email</TableCell>
              <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>Teléfono</TableCell>
              <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>Estado</TableCell>
              <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>Verificación</TableCell>
              <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>Saldo</TableCell>
              <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>Repartidor</TableCell>
              <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>Opciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transportDrivers?.map((row) => (
              <TableRow key={row.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                <TableCell component="th" scope="row">
                  {row.user.name}
                </TableCell>
                <TableCell component="th" scope="row">
                  {row.user.email}
                </TableCell>
                <TableCell component="th" scope="row">
                  {row.user.phone}
                </TableCell>
                <TableCell component="th" scope="row">
                  {row.isActive ? <Chip label="Activo" color="success" /> : <Chip label="Inactivo" color="error" />}
                </TableCell>
                <TableCell component="th" scope="row">
                  {row.isVerified ? (
                    <Chip label="Verificado" icon={<CheckCircle />} color="success" />
                  ) : (
                    <Chip label="Verificacion pendiente" icon={<Cancel />} color="error" />
                  )}
                </TableCell>
                <TableCell component="th" scope="row">
                  <Typography variant="body1" color="textSecondary">
                    {toCurrencyFormat(row.wallet?.currentBalance || 0)}
                  </Typography>
                </TableCell>
                <TableCell component="th" scope="row">
                  {row.user.driver !== null ? (
                    <Chip icon={<DeliveryDining />} color="success" />
                  ) : (
                    <Chip icon={<Cancel />} color="error" />
                  )}
                </TableCell>
                <TableCell>
                  <TransportDriverMenuItem
                    transportDriver={row}
                    handleOpenPersonalDataModal={() => openDetail(row.id)}
                    handleOpenTravelsModal={handleOpenTravelsModal}
                    handleVerifyClick={handleVerifyClick}
                    handleConvertToDeliveryDriver={handleConvertToDeliveryDriver}
                    handleOpenDepositModal={handleOpenModal}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Routes>
        <Route index element={null} />
        <Route path=":driverId/personal" element={<PersonalDataModal handleClose={() => navigate(-1)} />} />
      </Routes>
      {travelsModal && travels && (
        <CompletedTravelsModal open={travelsModal} handleClose={handleCloseTravelsModal} travels={travels} />
      )}
      {selectedDriver && (
        <CreateDepositModal
          isOpen={isModalOpen}
          handleClose={closeModal}
          isTransportDriver={true}
          id={selectedDriver.id}
          name={selectedDriver.name}
          phone={selectedDriver.phone}
          onSuccess={handleDepositSuccess}
        />
      )}
    </Box>
  );
};
